import moment from 'moment';
import { PlatformIChingConsultationExportDto } from '@data/_generated-api/platform-api/models/platform-i-ching-consultation-export-dto';
import { PlatformImportIChingConsultationItem } from '@data/_generated-api/platform-api/models/platform-import-i-ching-consultation-item';
import { IChingCastMode, IChingLineValue } from '../../core/iching.models';
import { parseLines } from '../hd-iching-journal.util';

/**
 * Het JSON-bestandsformaat van de dagboekexport (spec 2026-10-21-dagboek-momenten-export-design
 * §2.1). Puur: bouwen voor de export, lezen en voorvalideren voor de import. De server valideert
 * opnieuw en rekent de hexagrammen zelf uit de lijnen.
 */
export const ICHING_JOURNAL_FILE_FORMAT = 'egitura-classic-iching-journal';
export const ICHING_JOURNAL_FILE_VERSION = 1;
/** Spiegel van ModelConstraints.IChingImportMaxItems in de backend. */
export const ICHING_JOURNAL_IMPORT_MAX = 2000;

const QUESTION_KINDS: readonly string[] = ['question', 'situation'];
const CAST_MODES: readonly string[] = ['coins', 'manual', 'direct'];

export interface IChingJournalFileConsultation {
  readonly consultedAt: string;
  readonly createdOn: string | null;
  readonly updatedOn: string | null;
  readonly question: string;
  readonly questionKind: 'question' | 'situation';
  readonly castMode: IChingCastMode;
  /** Zes lijnwaarden, onder naar boven. */
  readonly lines: readonly IChingLineValue[];
  /** Zes worpen van drie muntzijden (H = kop, T = munt), onder naar boven. */
  readonly coinFaces: readonly string[] | null;
  /** Ter informatie voor de lezer; de import rekent opnieuw uit de lijnen. */
  readonly primaryHexagram: number | null;
  readonly changedHexagram: number | null;
  readonly keywords: readonly string[];
  readonly notes: string;
  readonly doList: readonly string[];
  readonly dontList: readonly string[];
  readonly revisitedOn: string | null;
  readonly linkedChart: { readonly id: string; readonly name: string | null } | null;
}

export interface IChingJournalFile {
  readonly format: typeof ICHING_JOURNAL_FILE_FORMAT;
  readonly version: number;
  readonly exportedAt: string;
  readonly consultations: readonly IChingJournalFileConsultation[];
}

export function buildJournalFile(rows: readonly PlatformIChingConsultationExportDto[], exportedAt: string): IChingJournalFile {
  return {
    format: ICHING_JOURNAL_FILE_FORMAT,
    version: ICHING_JOURNAL_FILE_VERSION,
    exportedAt,
    consultations: rows.map(row => ({
      consultedAt: row.consultedAt ?? row.createdOn ?? '',
      createdOn: row.createdOn ?? null,
      updatedOn: row.updatedOn ?? null,
      question: row.question ?? '',
      questionKind: row.questionKind === 'situation' ? 'situation' : 'question',
      castMode: (CAST_MODES.includes(row.castMode ?? '') ? row.castMode : 'manual') as IChingCastMode,
      lines: parseLines(row.lines),
      coinFaces: row.coinFaces ? row.coinFaces.split(',') : null,
      primaryHexagram: row.primaryHexagram ?? null,
      changedHexagram: row.changedHexagram ?? null,
      keywords: row.keywords ?? [],
      notes: row.notes ?? '',
      doList: row.doList ?? [],
      dontList: row.dontList ?? [],
      revisitedOn: row.revisitedOn ?? null,
      linkedChart: row.linkedChartId ? { id: row.linkedChartId, name: row.linkedChartName ?? null } : null,
    })),
  };
}

export function journalFileToJson(file: IChingJournalFile): string {
  return `${JSON.stringify(file, null, 2)}\n`;
}

export type IChingJournalFileError = 'notJson' | 'wrongFormat' | 'newerVersion' | 'empty' | 'tooMany';

/** Uitkomst van het inlezen; bij `ok: false` staat de reden in `error` en zijn er geen items. */
export interface IChingJournalParseResult {
  readonly ok: boolean;
  readonly error?: IChingJournalFileError;
  readonly items: readonly PlatformImportIChingConsultationItem[];
  /** Items die al in de browser niet klopten (geen geldige lijnen of tijdstip); niet verstuurd. */
  readonly invalid: number;
  /** Vroegste en laatste tijdstip van de geldige items (ISO). */
  readonly firstAt?: string;
  readonly lastAt?: string;
}

/** Leest een dagboekexport; alleen het eigen formaat tot en met de huidige versie. */
export function parseJournalFile(text: string): IChingJournalParseResult {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return failure('notJson');
  }
  if (!isRecord(data) || data['format'] !== ICHING_JOURNAL_FILE_FORMAT || !Array.isArray(data['consultations'])) {
    return failure('wrongFormat');
  }
  if (typeof data['version'] !== 'number' || data['version'] > ICHING_JOURNAL_FILE_VERSION) {
    return failure('newerVersion');
  }
  const raw = data['consultations'] as unknown[];
  if (raw.length === 0) return failure('empty');
  if (raw.length > ICHING_JOURNAL_IMPORT_MAX) return failure('tooMany');

  const items: PlatformImportIChingConsultationItem[] = [];
  let invalid = 0;
  for (const entry of raw) {
    const item = toImportItem(entry);
    if (item) items.push(item);
    else invalid++;
  }
  const times = items.map(item => item.consultedAt!).sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
  return { ok: true, items, invalid, firstAt: times[0], lastAt: times[times.length - 1] };
}

function failure(error: IChingJournalFileError): IChingJournalParseResult {
  return { ok: false, error, items: [], invalid: 0 };
}

function toImportItem(entry: unknown): PlatformImportIChingConsultationItem | undefined {
  if (!isRecord(entry)) return undefined;
  const lines = linesText(entry['lines']);
  const consultedAt = typeof entry['consultedAt'] === 'string' ? entry['consultedAt'] : '';
  if (!lines || !moment(consultedAt, moment.ISO_8601, true).isValid()) return undefined;
  const linkedChart = isRecord(entry['linkedChart']) ? entry['linkedChart'] : undefined;
  return {
    consultedAt,
    question: stringOr(entry['question'], ''),
    questionKind: QUESTION_KINDS.includes(stringOr(entry['questionKind'], '')) ? stringOr(entry['questionKind'], '') : 'question',
    castMode: CAST_MODES.includes(stringOr(entry['castMode'], '')) ? stringOr(entry['castMode'], '') : 'manual',
    lines,
    coinFaces: coinFacesText(entry['coinFaces']),
    keywords: stringList(entry['keywords']),
    notes: stringOr(entry['notes'], ''),
    doList: stringList(entry['doList']),
    dontList: stringList(entry['dontList']),
    revisitedOn: validIsoOrUndefined(entry['revisitedOn']),
    linkedChartId: linkedChart && typeof linkedChart['id'] === 'string' ? linkedChart['id'] : undefined,
    linkedChartName: linkedChart && typeof linkedChart['name'] === 'string' ? linkedChart['name'] : undefined,
  };
}

/** Lijnen als array (formaat) of als "7,8,..."-tekst (API-vorm); altijd zes geldige waarden. */
function linesText(value: unknown): string | undefined {
  const text = Array.isArray(value) ? value.join(',') : typeof value === 'string' ? value : '';
  const lines = parseLines(text);
  return lines.length === 6 ? lines.join(',') : undefined;
}

function coinFacesText(value: unknown): string | undefined {
  if (Array.isArray(value)) return value.every(group => typeof group === 'string') && value.length > 0 ? value.join(',') : undefined;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function validIsoOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && moment(value, moment.ISO_8601, true).isValid() ? value : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
